import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "block w-full" }
const _hoisted_2 = { class: "flex mb-4" }
const _hoisted_3 = { class: "ml-auto" }
const _hoisted_4 = { class: "buttonRowWrapper" }
const _hoisted_5 = ["title"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "icon-add" }, null, -1)
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = ["disabled", "title"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", { class: "icon-delete" }, null, -1)
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = { class: "text-center" }

import {BryntumGrid} from "@bryntum/grid-vue-3";
import {Store, StringHelper} from "@bryntum/grid";
import {onBeforeUnmount, onMounted, reactive, ref} from "vue";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage} from "@/AppConfig";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";
import DateToStringHelper from "@/utilities/helpers/DateToStringHelper";
import dialog from "@/components/dialog";
import {useI18n} from "vue-i18n";
import {useGlobalStore} from "@/stores/GlobalStore";
import TicketService from "@/utilities/services/entities/TicketService";
import {ErrorLocationType, PopupType, TicketType} from "@/utilities/enums/Enums";
import GridInputFilter from "@/components/GridInputFilter.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Error from "@/components/Error.vue";
import DotMenuFactoryHelper from "@/utilities/helpers/DotMenuFactoryHelper";
import RecipientService from "@/utilities/services/entities/RecipientService";
import router from "@/router";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import Ticket from "@/models/entities/Ticket";
import Recipient from "@/models/entities/Recipient";
import {updateServiceConfig} from "@/utilities/helpers/ServiceConfigHelper";
import {getGermanIsoString} from "@/utilities/helpers/GermanIsoStringHelper";



export default /*#__PURE__*/_defineComponent({
  __name: 'TicketTab',
  setup(__props) {

const {t} = useI18n();
const globalStore = useGlobalStore();

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};
const ticketService = new TicketService();
const recipientService = new RecipientService();

const selectedItems = ref<any[]>([]);
let clicked = false;


const gridDataIsLoading = ref<boolean>(false);
const gridStore = new Store({
  sorters: [{
    field: 'createdAt',
    ascending: false
  }]
})
const grid = ref(null)
const useGridConfig = {
  emptyText: t("general.noEntries"),
  headerMenuFeature: false,
  relayStoreEvents: true,
  cellTooltipFeature: {
    hoverDelay      : 300,
    textContent     : true,
    hideOnDelegateChange: true,
  },
  enableTextSelection: true,
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
  },
  columns : {
    data: [
      {
        id:'type',
        field: 'type',
        text: t('dashboard.tabs.ticket.grid.type'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${t(`dashboard.tabs.ticket.popups.edit.types.${TicketType[value]}`)}`;
        }
      },
      {
        id:'file.fileName',
        field: 'file.fileName',
        text: t('dashboard.tabs.ticket.grid.file'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${value ?? ""}`;
        }
      },
      {
        id:'recipientName',
        field: 'recipientName',
        text: t('dashboard.tabs.ticket.grid.recipientName'),
        flex: 1,
        type: "template",
        template: ({record}:any) => {
          return (record.originalData as Ticket).type === TicketType.staticDownload
              ? StringHelper.xss`${record.originalData.recipients.map((r:Recipient) => r.displayName).join(", ")}`
              : StringHelper.xss`${record.originalData.recipient.fullName}`;
        }
      },
      {
        id:'authToEmail',
        field: 'authToEmail',
        text: t('dashboard.tabs.ticket.grid.authToEmail'),
        flex: 1,
        type: "template",
        template: ({record}:any) => {
          return (record.originalData as Ticket).type === TicketType.staticDownload
              ? StringHelper.xss`${record.originalData.recipients.map((r:Recipient) => r.email).join(", ")}`
              : StringHelper.xss`${record.originalData.recipient.email}`;
        }
      },
      {
        id:'createdAt',
        field: 'createdAt',
        text: t('dashboard.tabs.ticket.grid.created'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          if(NullOrUndefinedHelper(value))return "";

          const date = new Date(getGermanIsoString(new Date(value)));
          const dateString = DateToStringHelper(date);
          return StringHelper.xss`${dateString}`;
        }
      },
      {
        id:'validity.expiresAt',
        field: 'validity.expiresAt',
        text: t('dashboard.tabs.ticket.grid.validToDate'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          if(NullOrUndefinedHelper(value))return "";

          const date = new Date(value);
          const dateString = DateToStringHelper(date);
          return StringHelper.xss`${dateString}`;
        }
      },
      {
        id: 'fileLinkButton',
        field: 'fileLinkButton',
        text: '',
        width: 36,
        minWidth: 49,
        resizable: false,
        sortable: false,
        tooltipRenderer : () => StringHelper.xss`<span class="text-[#013C74]">${t('general.showFiles')}</span>`,
        type: "template",
        template: ({record}:any) => {
          return record.originalData.type === TicketType.upload ? `<div class='gridRowButton'><span class='icon-reroute'></span></div>` : '';
        },
      },
      {
        id: 'editButton',
        field: 'editButton',
        text: '',
        width: 36,
        minWidth: 49,
        resizable: false,
        sortable: false,
        tooltipRenderer : () => StringHelper.xss`<span class="text-[#013C74]">${t('general.editAndResend')}</span>`,
        type: "template",
        template: () => {
          return `<div class='gridRowButton'><span class='icon-edit'></span></div>`;
        },
      },
      {
        id:'menu',
        text	: '',
        width   : 36,
        minWidth: 49,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-more-menu',
          onAction : ({ source: btn }:any) => {
            const menu = DotMenuFactoryHelper.build(btn.cellInfo.record.data, [
              {
                text: t('general.details'),
                cls: 'color',
                weight: 100,
                onItem : async ({menu}:any) => {
                  await openDetailPopup(menu.record)
                }
              },
              {
                text: t('general.edit'),
                cls: 'color',
                weight: 100,
                onItem : async ({ menu }:any) => {
                  openEditPopup(menu.record)
                }
              },
              {
                text: t('general.resend'),
                cls: 'color',
                disabled: NullOrUndefinedHelper(btn.cellInfo.record.data.recipient.fullName) && btn.cellInfo.record.data.type !== TicketType.staticDownload,
                weight: 100,
                onItem : async ({ menu }:any) => {
                  const resp = await ticketService.resend(menu.record as Ticket, ErrorLocationType.table)
                  if(!resp) return;
                  globalStore.showSuccess(t('general.send'))
                }
              },
              {
                text: t('general.passwordResend'),
                cls: 'color',
                hidden: btn.cellInfo.record.data.type !== TicketType.staticDownload,
                weight: 100,
                onItem : async ({ menu }:any) => {
                  await ticketService.resendPasswordMail(menu.record as Ticket, ErrorLocationType.table);
                }
              },
              {
                text: t('general.delete'),
                cls: 'color',
                weight: 100,
                onItem : async ({ menu }:any) => {
                  const answer = await dialog
                      .okText(t("general.delete"))
                      .cancelText(t("general.cancel"))
                      .title(t("general.deleteEntity", {entity: t("general.entities.ticket")}))
                      .confirm(t("dashboard.tabs.ticket.confirmDeletionSingle", {name: menu.record.name}));

                  if (answer) {
                    const resp = await ticketService.delete(menu.record as Ticket, ErrorLocationType.table);
                    if(!resp) return;

                    gridStore.applyChangeset({removed:[{id:menu.record.id}]});
                    globalStore.showSuccess(t('general.deleted'));
                  }
                }
              }
            ]);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      }
    ],
    listeners:{
    }
  },
  store: gridStore,
  listeners: {
    selectionChange(event:any) {
      if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedItems.value = selectedItems.value.concat(event.selected.map(f=> f.data))
      selectedItems.value = selectedItems.value.filter(f => !event.deselected.map(fd => fd.data).some(fd => fd?.id === f?.id));
    },
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.getCount();
    },
    async cellClick({column, record}:any){
      if(clicked) return;
      clicked = true;

      if(column.data.field === 'editButton'){
        openEditPopup(record.originalData);
      }
      if(column.data.field === 'resendButton'){
        const resp = await ticketService.resend(record.originalData as Ticket, ErrorLocationType.table)
        if(!resp) {
          clicked = false;
          return;
        }
        //TODO update ticket entry in table?
        globalStore.showSuccess(t('general.send'))
      }

      if(column.data.field === 'fileLinkButton'){
       if ((record.originalData).type !== TicketType.upload) {
         clicked = false;
         return;
       }

       await router.push(`/dashboard?ticket=${(record.originalData).urlToken}`, );
       // force nativgation/reload
       router.go(0)
      }

      clicked = false;
    }
  },
  height: currentWindowHeight,
}
const gridConfig = reactive(useGridConfig)


const setGridData = async () => {
  const resp = await ticketService.getForUser(ErrorLocationType.table)
  if(!resp) return;

  gridStore.data = resp.map((t:any) => {
    t.ghostType = TicketType[t.type];
    return t;
  });
}

const openEditPopup = (ticket:Ticket|null = null) => {
  // catch compatibility issues and show different popup
  if (ticket?.recipient.fullName === null && ticket?.id >= 0 && ticket?.type !== TicketType.staticDownload) {
    globalStore.openPopup({
      data: {textToShow: t('dashboard.tabs.file.popups.edit.helpText.compatibility')},
      resetButton:false,
      title: t('general.caution'),
      type: PopupType.genericText,
      big:true
    });
    return
  }

  globalStore.openPopup({
    data: ticket ?? new Ticket(),
    action: async () => {
      const ticketFromPopup = globalStore.modal.contentObject as Ticket;
      const isNewStaticTicket = ticketFromPopup.type === TicketType.staticDownload && ticketFromPopup.id < 0;
      const newRecipient = ticketFromPopup.newRecipient;
      let ticket = ticketFromPopup as Ticket;
      const isEdit = ticketFromPopup.id >= 0;

      if(isNewStaticTicket) {
        const resp = await ticketService.createStatic(ticket, ErrorLocationType.popup)
        if (!resp) return;

        // take over the recipient meta-data
        const recipients = ticket.recipients;
        const recipientLists = ticket.recipientlists;
        const newRecipient = ticket.newRecipient;
        ticket = resp
        ticket.recipients = recipients;
        ticket.recipientlists = recipientLists;
        ticket.newRecipient = newRecipient;
      }

      if(ticket.id >= 0 && !isNewStaticTicket) {
        const resp = await ticketService.update(ticket, ErrorLocationType.popup)
        if (!resp) return;
      }

      // generate new recipient if needed
      if(!NullOrUndefinedHelper(newRecipient?.email)) {
        const creationResp = await recipientService.create(newRecipient!, ErrorLocationType.popup);
        if(!creationResp) return;

        if(!ticket.recipients)
          ticket.recipients = []

        // append him to recipeint list to generate ticket later on
        ticket.recipients.push(creationResp);
      }

      // generate tickets for recipients
      //TODO send static tickets when needed
      const recipients = ticket.recipients;
      const recipientlists = ticket.recipientlists;
      const needsTicketGeneration = recipientlists && recipientlists.some(()=>true) || recipients && recipients.some(()=>true)

      if(!isEdit && !needsTicketGeneration && ticketFromPopup.type !== TicketType.staticDownload){
        await ErrorHandler.showLocalError(t('general.validation.recipientsRequired'), ErrorLocationType.popup);
        return;
      }

      if(needsTicketGeneration) {
        const generatedTickets = await ticketService.generate(ticket, ErrorLocationType.popup);
        if (!generatedTickets) return;

        if(ticket.type !== TicketType.staticDownload) {
          for (const generatedTicket of generatedTickets) {
            gridStore.add(generatedTicket);
            await gridStore.sort(gridStore.sorters);
          }
        }
        else {
          gridStore.add(ticket);
          await gridStore.sort(gridStore.sorters);
        }
      }
      else {
        gridStore.add(ticket);
        await gridStore.sort(gridStore.sorters);
      }

      if(ticketFromPopup.type === TicketType.staticDownload && isNewStaticTicket) {
        globalStore.modal.contentObject = ticket;
        globalStore.modal.title = t("dashboard.tabs.ticket.popups.edit.title.update");
        globalStore.showSuccess(t("general.generated"));
      }
      else {
        globalStore.closeModal();
        globalStore.showSuccess(t("general.saved"));
      }
    },
    resetButton:false,
    title: ticket
        ? t("dashboard.tabs.ticket.popups.edit.title.update")
        : t("dashboard.tabs.ticket.popups.edit.title.add"),
    type: PopupType.ticketEdit,
    big:true
  })
}

const openDetailPopup = async (ticket:Ticket) => {
  globalStore.openPopup({
    data: ticket,
    resetButton:false,
    title: t("dashboard.tabs.ticket.popups.details.title"),
    type: PopupType.ticketDetails,
    big:true
  })
}

const deleteSelection = async () => {
  if(!selectedItems.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t('general.delete'))
      .cancelText(t('general.cancel'))
      .title(t("general.deleteEntity", {entity: t("general.entities.ticket")}))
      .confirm(t('dashboard.tabs.ticket.confirmDeletionMulti', {count:selectedItems.value.length}));

  if (answer) {
    let someEntriesFailed = false;

    for (const ticket of selectedItems.value){
      const resp = await ticketService.delete(ticket, ErrorLocationType.table);
      if(!resp){
        someEntriesFailed = true;
        continue;
      }

      gridStore.applyChangeset({removed:[{id:ticket.id}]});
    }

    if (someEntriesFailed) return;

    globalStore.showSuccess(t("general.deleted"));
  }
}


onMounted(async ()=> {
  recalcGridHeight()
  window.addEventListener('resize', recalcGridHeight)

  gridDataIsLoading.value = true
  await updateServiceConfig();
  await setGridData();
  gridDataIsLoading.value = false
})

onBeforeUnmount(()=> {
  window.removeEventListener('resize', recalcGridHeight)

  globalStore.gridStore = null
  globalStore.gridColumnStore = null
  globalStore.currentItems = 0
  globalStore.allItems = 0
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Error, {
        errorLoc: _unref(ErrorLocationType).table
      }, null, 8, ["errorLoc"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(GridInputFilter, {
              gridStore: _unref(gridStore),
              hideFilterButton: true
            }, null, 8, ["gridStore"])
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("button", {
            class: "primary symetric36 mr-2",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (openEditPopup())),
            title: _unref(t)('general.add')
          }, _hoisted_7, 8, _hoisted_5),
          _createElementVNode("button", {
            class: "primary symetric36 mr-2",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (deleteSelection())),
            disabled: !selectedItems.value.some(()=>true),
            title: _unref(t)('general.delete')
          }, _hoisted_10, 8, _hoisted_8)
        ]),
        _createVNode(_unref(BryntumGrid), _mergeProps({
          ref_key: "grid",
          ref: grid
        }, gridConfig, { readOnly: true }), null, 16)
      ])
    ], 512), [
      [_vShow, !gridDataIsLoading.value]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_11, [
      _createVNode(LoadingSpinner)
    ], 512), [
      [_vShow, gridDataIsLoading.value]
    ])
  ]))
}
}

})